import { useEffect, useRef, useState } from "react";
import { Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAdminApi } from "@/hooks/useAdminApi";
import { errMsg } from "@/lib/api";
import { Bubble, usePolledMessages } from "@/components/store/ChatWidget";

const Thread = ({ chat, onBack }) => {
  const call = useAdminApi();
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const [msgs, push] = usePolledMessages((since) => call("get", `/admin/chats/${chat.id}/messages`, null, { params: since ? { since } : {} }), chat.id);
  useEffect(() => { const el = endRef.current?.parentElement; if (el) el.scrollTop = el.scrollHeight; }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { push(await call("post", `/admin/chats/${chat.id}/messages`, { text })); setText(""); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 h-14 border-b border-white/10 flex items-center gap-3">
        <button data-testid="admin-chat-back" onClick={onBack} aria-label="Back to conversations" className="md:hidden -ml-2 p-2 text-gray-400 hover:text-white"><ArrowLeft size={18} /></button>
        <div className="min-w-0">
        <p className="font-semibold text-sm truncate">{chat.name || "Visitor"}</p>
        <p className="text-xs text-gray-500">{chat.email ? <a data-testid="admin-chat-email-link" href={`mailto:${chat.email}`} className="hover:text-emerald-400 hover:underline">{chat.email}</a> : "no email given"}</p>
        </div>
      </div>
      <div data-testid="admin-chat-messages" className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender === "admin"} />)}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply..."
          className="w-full h-10 px-3 rounded-md bg-[#08090C] border border-white/10 text-sm focus:outline-none focus:border-emerald-500/60" />
        <button data-testid="admin-chat-send" className="w-10 h-10 shrink-0 grid place-items-center rounded-md bg-emerald-500 text-[#08090C] hover:bg-emerald-400"><Send size={16} /></button>
      </form>
    </div>
  );
};

export const ChatsTab = ({ onChange, initialId }) => {
  const call = useAdminApi();
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    const load = () => call("get", "/admin/chats").then((cs) => {
      setChats(cs);
      if (initialId) setActive((a) => a || cs.find((c) => c.id === initialId) || null);
    }).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="grid md:grid-cols-[300px_1fr] rounded-lg border border-white/10 bg-[#0F1117] h-[calc(100dvh-14rem)] min-h-[420px] md:h-[600px] overflow-hidden">
      <div className={`border-r border-white/10 overflow-y-auto ${active ? "hidden md:block" : ""}`}>
        {chats.length === 0 && <p data-testid="admin-chats-empty" className="p-6 text-sm text-gray-500">No conversations yet.</p>}
        {chats.map((c) => (
          <button key={c.id} data-testid={`admin-chat-item-${c.id}`} onClick={() => { setActive(c); onChange(); }}
            className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] ${active?.id === c.id ? "bg-white/5" : ""}`}>
            <div className="flex justify-between gap-2">
              <span className="font-semibold text-sm truncate">{c.name || c.email || "Visitor"}</span>
              {c.unread_admin > 0 && active?.id !== c.id && <span className="text-[10px] font-bold bg-emerald-500 text-[#08090C] rounded-full px-2 py-0.5">{c.unread_admin}</span>}
            </div>
            <p className="text-xs text-gray-500 truncate mt-1">{c.last_message}</p>
            <p className="text-[10px] text-gray-600 mt-1">{new Date(c.last_message_at).toLocaleString()}</p>
          </button>
        ))}
      </div>
      <div className={`min-h-0 h-full ${active ? "" : "hidden md:block"}`}>{active ? <Thread key={active.id} chat={active} onBack={() => setActive(null)} /> : <p className="p-10 text-sm text-gray-500">Select a conversation.</p>}</div>
    </div>
  );
};
